import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase, getAuthToken } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import { usePracticienKey } from "../lib/useCrypto";
import { decryptFernet } from "../lib/fernetUtils";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import * as Popover from "@radix-ui/react-popover";
import * as Dialog from "@radix-ui/react-dialog";
import { UserPlus, MessageSquarePlus, Search, CalendarDays, AlertTriangle, Pill } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BotStatusListener from "../components/BotStatusListener";
import StatusContextMenu, { type ConversationStatus } from "../components/StatusContextMenu";
import ReopeningDigestCta from "../components/ReopeningDigestCta";
import ReopeningDigestThreadRow from "../components/ReopeningDigestThreadRow";
import { usePatientDirectory } from "../lib/PatientDirectoryContext";
import { usePermissions } from "../lib/usePermission";
import { useStaff } from "../lib/StaffContext";
import { getSidebarIdentityLabel } from "../lib/sidebarIdentity";
import {
  findConversationLabel,
  getConversationLabelDotClass,
  mergeConversationRowsForRefresh,
} from "../lib/conversationLabels.mjs";
import { resolveConversationBotOff } from "../lib/conversationBotStatus.mjs";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

const AVATAR_PALETTES = [
  { bg: "bg-teal-100",    text: "text-teal-700",    ring: "ring-teal-200" },
  { bg: "bg-violet-100",  text: "text-violet-700",  ring: "ring-violet-200" },
  { bg: "bg-amber-100",   text: "text-amber-700",   ring: "ring-amber-200" },
  { bg: "bg-rose-100",    text: "text-rose-700",    ring: "ring-rose-200" },
  { bg: "bg-sky-100",     text: "text-sky-700",     ring: "ring-sky-200" },
  { bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-200" },
];

function getAvatarPalette(phone: string) {
  const hash = phone.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_PALETTES[hash % AVATAR_PALETTES.length];
}

type Row = {
  id: string;
  utilisateur: string;
  role: "user" | "assistant" | "human";
  contenu_chiffre: string;
  created_at: string;
  profile_name?: string | null;
  praticien_id: string;
  lu?: boolean;
  tag?: string | null;
  conversation_status?: string | null;
  assigned_to?: string | null;
  assigned_at?: string | null;
  assignment_seen?: boolean;
  assignment_comment?: string | null;
  unread_count?: number;
  botOff?: boolean;
};

type Thread = {
  utilisateur: string;
  displayPhone: string;
  displayName: string;
  lastText: string;
  lastAt: string;
  isUnread: boolean;
  unreadCount: number;
  botOff: boolean;
  tag?: string | null;
  conversation_status?: string | null;
  assigned_to?: string | null;
  assignment_seen?: boolean;
  assignment_comment?: string | null;
};

type Practitioner = { id: string; full_name: string };

function formatHour(d: string) {
  const date = new Date(d);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatLastAt(d: string) {
  const date = dayjs(d);

  if (date.isToday()) {
    return `Today ${date.format("HH:mm")}`;
  }
  if (date.isYesterday()) {
    return `Yesterday ${date.format("HH:mm")}`;
  }

  const diffDays = dayjs().diff(date, "day");
  if (diffDays <= 6) {
    // Jour de la semaine
    return date.format("dddd");
  }

  // Sinon date complète
  return date.format("DD/MM/YYYY");
}

const HIGHLIGHT_COLOR_MAP: Record<string, { bg: string; border: string }> = {
  blue:   { bg: "bg-blue-50",   border: "border-blue-500" },
  green:  { bg: "bg-green-50",  border: "border-green-400" },
  yellow: { bg: "bg-yellow-50", border: "border-yellow-300" },
  red:    { bg: "bg-red-50",    border: "border-red-400" },
  purple: { bg: "bg-purple-50", border: "border-purple-400" },
  pink:   { bg: "bg-pink-50",   border: "border-pink-400" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-400" },
  gray:   { bg: "bg-gray-50",   border: "border-gray-300" },
};

const LONG_PRESS_DURATION_MS = 550;
const LONG_PRESS_MOVE_CANCEL_PX = 10;
const INITIAL_DAYS_WINDOW = 2;
const LOAD_MORE_DAYS_STEP = 2;
const MAX_DAYS_WINDOW = 90;
const CONVERSATION_SEARCH_DAYS = 90;
const CONVERSATION_SEARCH_DEBOUNCE_MS = 300;

function getHighlightClasses(color: string | null): string {
  if (!color) return "";
  const c = HIGHLIGHT_COLOR_MAP[color];
  if (!c) return "";
  return `${c.bg} border-l-4 ${c.border}`;
}

// 🔒 Cache local des déchiffrements
const localDecryptCache: Map<string, string> = new Map();

// Chargement initial du cache depuis localStorage (si présent)
try {
  const stored = localStorage.getItem("decryptCache");
  if (stored) {
    const parsed = JSON.parse(stored);
    for (const [k, v] of Object.entries(parsed)) {
      localDecryptCache.set(k, v as string);
    }
  }
} catch (err) {
  console.warn("[cache] Erreur de lecture du cache local:", err);
}

export default function ConversationsList({
  activeDoctor,
  selectedUser,
  onSelectItem,
}: {
  activeDoctor?: any;
  selectedUser?: string | null;
  /** Optional callback for desktop layout to intercept item selection (digest or conversation) */
  onSelectItem?: (routeId: string) => void;
}) {
  const user = useUser();
  const { key: practKey } = usePracticienKey();
  const { getDisplayNameForPhone } = usePatientDirectory();
  const permissions = usePermissions();
  const { activeStaff } = useStaff();
  // ID used for "assigned to me" comparisons:
  // Level 3 RBAC → staff_id, Level 2 → activeDoctor.id, Level 1 → null
  const assigneeId = activeStaff?.staff_id ?? activeDoctor?.id ?? null;
  const [rows, setRows] = useState<Row[]>([]);
  const [daysWindow, setDaysWindow] = useState(INITIAL_DAYS_WINDOW); // 48h = 2 jours
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRealtimeActive, setIsRealtimeActive] = useState(true);
  const navigate = useNavigate();
  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  const [filter, setFilter] = useState<"all" | "unread" | "assigned" | "all_tasks">("all");
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [hasMultiple, setHasMultiple] = useState(false);
  const [assignedCount, setAssignedCount] = useState(0);

  const [highlightUnread, setHighlightUnread] = useState<string | null>("blue");
  const [highlightRead, setHighlightRead] = useState<string | null>(null);

  const [statusTagsEnabled, setStatusTagsEnabled] = useState(false);
  const [clinicStatuses, setClinicStatuses] = useState<ConversationStatus[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; utilisateur: string } | null>(null);
  const [mobileLabelSheetUser, setMobileLabelSheetUser] = useState<string | null>(null);

  // ── Reopening digest state ─────────────────────────────────────────────
  const [digestFeatureEnabled, setDigestFeatureEnabled] = useState(false);
  const [digestClinicOpen, setDigestClinicOpen] = useState(false);
  const [digestHasWindow, setDigestHasWindow] = useState(false);
  const [digestCurrent, setDigestCurrent] = useState<{
    id: string;
    status: string;
    window_start: string;
    window_end: string;
    metrics_json: Record<string, number>;
    active_metrics?: Record<string, number> | null;
    hidden_for_viewer: boolean;
  } | null>(null);
  const [digestGenerating, setDigestGenerating] = useState(false);
  // When true the digest row is visible (not hidden for this viewer)
  const [digestVisible, setDigestVisible] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchIndex, setSearchIndex] = useState<Record<string, string>>({});
  const [serverSearchRows, setServerSearchRows] = useState<Row[]>([]);
  const [serverSearchMatches, setServerSearchMatches] = useState<Record<string, boolean>>({});
  // Mémorise le dernier nom exploitable vu par numéro pour survivre
  // aux lignes "thread unique" qui n'embarquent pas toujours profile_name.
  const [knownNames, setKnownNames] = useState<Record<string, string>>({});
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);
  const [selectedAssignUser, setSelectedAssignUser] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const mobileLabelLongPressTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const mobileLabelPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const suppressNextConversationClickRef = useRef(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const currentPractitionerId = useMemo(
    () => (user?.role === "admin" ? activeDoctor?.id : user?.id) || null,
    [activeDoctor?.id, user?.id, user?.role]
  );

  const cancelMobileLabelLongPress = useCallback(() => {
    if (mobileLabelLongPressTimerRef.current) {
      window.clearTimeout(mobileLabelLongPressTimerRef.current);
      mobileLabelLongPressTimerRef.current = null;
    }
    mobileLabelPressStartRef.current = null;
  }, []);

  const startMobileLabelLongPress = useCallback(
    (thread: Thread, event: ReactPointerEvent<HTMLLIElement>) => {
      if (!isMobile || !statusTagsEnabled || clinicStatuses.length === 0) return;
      if (event.pointerType === "mouse") return;

      cancelMobileLabelLongPress();
      suppressNextConversationClickRef.current = false;
      mobileLabelPressStartRef.current = { x: event.clientX, y: event.clientY };
      mobileLabelLongPressTimerRef.current = window.setTimeout(() => {
        suppressNextConversationClickRef.current = true;
        mobileLabelLongPressTimerRef.current = null;
        mobileLabelPressStartRef.current = null;
        setMobileLabelSheetUser(thread.utilisateur);
      }, LONG_PRESS_DURATION_MS);
    },
    [cancelMobileLabelLongPress, clinicStatuses.length, isMobile, statusTagsEnabled]
  );

  const cancelMobileLabelLongPressOnMove = useCallback(
    (event: ReactPointerEvent<HTMLLIElement>) => {
      const start = mobileLabelPressStartRef.current;
      if (!start) return;

      const moved =
        Math.abs(event.clientX - start.x) > LONG_PRESS_MOVE_CANCEL_PX ||
        Math.abs(event.clientY - start.y) > LONG_PRESS_MOVE_CANCEL_PX;
      if (moved) cancelMobileLabelLongPress();
    },
    [cancelMobileLabelLongPress]
  );

  const suppressConversationClickAfterLongPress = useCallback(
    (event: ReactMouseEvent<HTMLLIElement>) => {
      if (!suppressNextConversationClickRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation?.();
      window.setTimeout(() => {
        suppressNextConversationClickRef.current = false;
      }, 0);
    },
    []
  );

  useEffect(() => {
    return () => cancelMobileLabelLongPress();
  }, [cancelMobileLabelLongPress]);

  // ───────────────────────────────────────────────
  // 👩‍⚕️ Chargement des praticiens de la clinique
  // ───────────────────────────────────────────────
  useEffect(() => {
    async function fetchPractitioners() {
      if (!user || user.role === "admin") return;
      const { data, error } = await supabase
        .from("clinic_practitioners")
        .select("id, full_name")
        .eq("clinic_id", user.id)
        .order("full_name", { ascending: true });
  
      if (!error && data) {
        setPractitioners(data as Practitioner[]);
        setHasMultiple((data?.length || 0) > 1);
      }
    }
    fetchPractitioners();
  }, [user]);

  // ───────────────────────────────────────────────
  // 🎨 Chargement des couleurs de surbrillance
  // ───────────────────────────────────────────────
  useEffect(() => {
    async function fetchHighlightSettings() {
      if (!user || !user.twilio_numero) return;
      const { data } = await supabase
        .from("clients")
        .select("highlight_unread, highlight_read, status_tags_enabled, conversation_statuses")
        .eq("twilio_numero", user.twilio_numero)
        .maybeSingle();
      if (data) {
        setHighlightUnread(data.highlight_unread || "blue");
        setHighlightRead(data.highlight_read || null);
        setStatusTagsEnabled(!!data.status_tags_enabled);
        if (Array.isArray(data.conversation_statuses)) {
          setClinicStatuses(data.conversation_statuses as ConversationStatus[]);
        }
      }
    }
    fetchHighlightSettings();
  }, [user]);

  // Close status dropdown on outside click
  useEffect(() => {
    if (!statusDropdownOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [statusDropdownOpen]);

  // ---------------------------------------------------------------
  // 📡 REALTIME : INSERT + UPDATE
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!user) return;
  
    const praticienId =
      user.role === "admin" ? activeDoctor?.id : user.id;
  
    if (!praticienId) return;
  
    const channel = supabase
      .channel("conv-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversations",
          filter: `praticien_id=eq.${praticienId}`,
        },
        async (payload) => {
          const row = payload.new || payload.old;
          if (!row) return;
  
          // Fonction utilitaire : reconstruit un thread cohérent
          const buildThread = (message: any, unread_count: number) => ({
            ...message,
            unread_count,
            botOff: false,
            assigned_to: message.assigned_to ?? null,
            assignment_seen: message.assignment_seen ?? null,
            assignment_comment: message.assignment_comment ?? null,
          });
  
          // ---------------------------------------------------------------
          // 🟠 INSERT : toujours considérer le thread comme NON LU
          // ---------------------------------------------------------------
          if (payload.eventType === "INSERT") {
            setRows((prev) => {
              const existing = prev.find((p) => p.utilisateur === row.utilisateur);
              const others = prev.filter((p) => p.utilisateur !== row.utilisateur);

              // Un message compte comme "non-lu" seulement si PATIENT / ASSISTANT
              const isCountable = row.role === "user" || row.role === "assistant";
              const previousUnread = existing?.unread_count ?? 0;
              const enriched = buildThread(row, isCountable ? previousUnread + 1 : previousUnread);

              // Preserve user-assigned status when a new message arrives
              if (!enriched.conversation_status && existing?.conversation_status) {
                enriched.conversation_status = existing.conversation_status;
              }

              const merged = [...others, enriched];

              // Tri DESC par date
              return merged.sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime()
              );
            });
          }
  
          // ---------------------------------------------------------------
          // 🔵 UPDATE : préserver created_at et la position dans la liste.
          // Ne mettre à jour que les champs d’état (lu, tag, assigned_to…).
          // Évite la race condition : Supabase envoie un UPDATE par ligne
          // quand on marque un thread entier comme lu, et le dernier UPDATE
          // traité pouvait avoir un ancien created_at → réordonnancement.
          // ---------------------------------------------------------------
          if (payload.eventType === "UPDATE") {
            setRows((prev) =>
              prev.map((r) => {
                if (r.utilisateur !== row.utilisateur) return r;
                return {
                  ...r,
                  lu: row.lu,
                  tag: row.tag ?? r.tag,
                  conversation_status: row.conversation_status !== undefined ? row.conversation_status : r.conversation_status,
                  assigned_to: row.assigned_to ?? r.assigned_to,
                  assigned_at: row.assigned_at ?? r.assigned_at,
                  assignment_seen: row.assignment_seen ?? r.assignment_seen,
                  assignment_comment: row.assignment_comment ?? r.assignment_comment,
                  unread_count: row.lu === true ? 0 : r.unread_count,
                };
              })
            );
          }
        }
      )
      .on("system", { event: "*" } as any, (status: string) => {
        if (status === "closed" || status === "errored") {
          setIsRealtimeActive(false);
        } else if (status === "joined") {
          setIsRealtimeActive(true);
        }
      })
      .subscribe();
  
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, activeDoctor?.id]);
  
  // ---------------------------------------------------------------
  // Helper : mapper la réponse API en Row[]
  // ---------------------------------------------------------------
  const mapThreadResponse = (threads: any[], praticienId: string): Row[] =>
    threads.map((t: any) => ({
      id: t.id,
      utilisateur: t.utilisateur,
      role: t.role,
      contenu_chiffre: t.contenu_chiffre,
      created_at: t.created_at,
      profile_name: t.profile_name,
      praticien_id: praticienId,
      lu: t.lu,
      tag: t.tag,
      conversation_status: t.conversation_status ?? null,
      unread_count: t.unread_count ?? 0,
      botOff: t.bot_status === "off",
      assigned_to: t.assigned_to ?? null,
      assigned_at: t.assigned_at ?? null,
      assignment_seen: t.assignment_seen ?? null,
      assignment_comment: t.assignment_comment ?? null,
    }));

  const buildThreadsListUrl = (praticienId: string, days: number, page: number) => {
    const params = new URLSearchParams({
      praticien_id: praticienId,
      days: String(days),
      page: String(page),
    });
    return `${import.meta.env.VITE_API_URL}/internal/conversations/list?${params.toString()}`;
  };

  const fetchThreadsList = async (praticienId: string, days: number) => {
    const token = await getAuthToken();
    const requestOptions = token ? { headers: { Authorization: token } } : undefined;
    const allThreads: any[] = [];
    const seenPages = new Set<number>();
    let nextPage: number | null = 1;

    while (nextPage !== null) {
      const page = nextPage;
      if (seenPages.has(page)) break;
      seenPages.add(page);

      const res = await fetch(buildThreadsListUrl(praticienId, days, page), requestOptions);
      if (!res.ok) {
        throw new Error(`Failed to load conversation threads page ${page}`);
      }
      const json = await res.json();
      allThreads.push(...(json.threads || []));

      const candidate = Number(json.next_page);
      nextPage = Number.isInteger(candidate) && candidate > page ? candidate : null;
    }

    return mapThreadResponse(allThreads, praticienId);
  };

  // ── Digest: fetch current state ────────────────────────────────────────
  const fetchDigestStatus = useCallback(async (praticienId: string) => {
    try {
      const token = await getAuthToken();
      const staffId = activeStaff?.staff_id;
      const params = new URLSearchParams({ praticien_id: praticienId });
      if (staffId) params.set("staff_id", staffId);

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/internal/reopening-digests/current?${params}`,
        token ? { headers: { Authorization: token } } : {}
      );
      if (!res.ok) return;
      const data = await res.json();

      setDigestFeatureEnabled(!!data.feature_enabled);
      setDigestClinicOpen(!!data.clinic_open);
      setDigestHasWindow(!!data.window);

      if (data.digest) {
        setDigestCurrent({
          ...data.digest,
          active_metrics: data.digest.active_metrics ?? null,
        });
        setDigestVisible(!data.digest.hidden_for_viewer);
      } else {
        setDigestCurrent(null);
        setDigestVisible(false);
      }
    } catch {
      // silent — digest is non-critical
    }
  }, [activeStaff?.staff_id]);

  // ── Digest: generate ───────────────────────────────────────────────────
  const handleGenerateDigest = useCallback(async () => {
    const praticienId = currentPractitionerId;
    if (!praticienId) return;
    setDigestGenerating(true);
    try {
      const token = await getAuthToken();
      const body: Record<string, string> = { praticien_id: praticienId };
      const actorLabel = getSidebarIdentityLabel(activeStaff, activeDoctor, user);
      if (activeStaff?.staff_id) body.staff_id = activeStaff.staff_id;
      if (actorLabel) body.actor_label = actorLabel;

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/internal/reopening-digests/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify(body),
        }
      );
      if (res.ok) {
        // Refresh status after triggering
        await fetchDigestStatus(praticienId);
      }
    } catch {
      // silent
    } finally {
      setDigestGenerating(false);
    }
  }, [
    currentPractitionerId,
    activeDoctor?.full_name,
    activeStaff?.name,
    activeStaff?.staff_id,
    fetchDigestStatus,
    user?.email,
  ]);

  // ── Digest: reopen (un-hide) ───────────────────────────────────────────
  const handleReopenDigest = useCallback(() => {
    if (digestCurrent) {
      setDigestVisible(true);
    }
  }, [digestCurrent]);

  // ── Digest: on hidden callback ─────────────────────────────────────────
  const handleDigestHidden = useCallback(() => {
    setDigestVisible(false);
  }, []);

  // ---------------------------------------------------------------
  // 🔄 FETCH INITIAL : backend (RPC via /internal) + cache Redis
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!user) return;

    let isActive = true;

    async function fetchThreads() {
      if (!isActive) return;

      const praticienId =
        user.role === "admin" ? activeDoctor?.id : user.id;

      if (!praticienId) return;

      try {
        const mapped = await fetchThreadsList(praticienId, INITIAL_DAYS_WINDOW);
        if (isActive) setRows((prev) => mergeConversationRowsForRefresh(prev, mapped));
      } catch (err) {
        console.error("[THREADS][ERROR]", err);
      }

      // Fetch digest status alongside thread list load
      if (isActive) {
        fetchDigestStatus(praticienId);
      }
    }

    fetchThreads();

    return () => {
      isActive = false;
    };
  }, [user?.id, activeDoctor?.id]);

  // ---------------------------------------------------------------
  // 🔄 POLLING FALLBACK : quand le realtime est down
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!user || isRealtimeActive) return;

    const praticienId =
      user.role === "admin" ? activeDoctor?.id : user.id;
    if (!praticienId) return;

    const refreshThreads = async () => {
      try {
        const mapped = await fetchThreadsList(praticienId, daysWindow);
        setRows((prev) => mergeConversationRowsForRefresh(prev, mapped));
      } catch (err) {
        console.error("[POLL][ERROR]", err);
      }
    };

    refreshThreads();
    const interval = setInterval(refreshThreads, 5000);
    return () => clearInterval(interval);
  }, [user?.id, activeDoctor?.id, isRealtimeActive, daysWindow]);

  // ---------------------------------------------------------------
  // 👁️ VISIBILITY : refetch quand l'onglet redevient visible
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!user) return;

    const praticienId =
      user.role === "admin" ? activeDoctor?.id : user.id;
    if (!praticienId) return;

    const handleVisibilityChange = async () => {
      if (document.hidden) return;
      try {
        const mapped = await fetchThreadsList(praticienId, daysWindow);
        setRows((prev) => mergeConversationRowsForRefresh(prev, mapped));
      } catch (err) {
        console.error("[VISIBILITY][ERROR]", err);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user?.id, activeDoctor?.id, daysWindow]);

  // ── Digest: react to hide from the detail view ─────────────────────────
  useEffect(() => {
    const praticienId = currentPractitionerId;
    const handleHide = () => {
      setDigestVisible(false);
      if (praticienId) fetchDigestStatus(praticienId);
    };
    const handleUpdate = () => {
      if (praticienId) fetchDigestStatus(praticienId);
    };
    window.addEventListener("digest-hidden", handleHide);
    window.addEventListener("digest-updated", handleUpdate);
    return () => {
      window.removeEventListener("digest-hidden", handleHide);
      window.removeEventListener("digest-updated", handleUpdate);
    };
  }, [currentPractitionerId, fetchDigestStatus]);


  // ---------------------------------------------------------------
  // 🟢 SYNC : clear assignment (événement interne)
  // ---------------------------------------------------------------
  useEffect(() => {
    function handleCleared(e: any) {
      const userId = e.detail;
      setRows(prev =>
        prev.map(r =>
          r.utilisateur === userId
            ? {
                ...r,
                assigned_to: null,
                assigned_at: null,
                assignment_seen: true,
                assignment_comment: null,
              }
            : r
        )
      );
    }
  
    window.addEventListener("assignment-cleared", handleCleared);
    return () =>
      window.removeEventListener("assignment-cleared", handleCleared);
  }, []);

  // 🟢 SYNC : quand un thread est ouvert → marqué comme lu côté liste
  useEffect(() => {
    function handleThreadRead(e: any) {
      const userId = e.detail as string;
  
      setRows(prev =>
        prev.map(r =>
          r.utilisateur === userId
            ? {
                ...r,
                lu: true,
                // on force à 0 pour l'affichage immédiat
                unread_count: 0,
              }
            : r
        )
      );
    }
  
    window.addEventListener("thread-read", handleThreadRead);
    return () =>
      window.removeEventListener("thread-read", handleThreadRead);
  }, []);

  // Construit un cache local "numéro -> dernier nom connu" à partir des rows
  // chargées en mémoire (fetch initial, realtime, load more). On n'écrit l'état
  // que si une vraie différence existe pour éviter des renders inutiles.
  useEffect(() => {
    setKnownNames((prev) => {
      let hasChange = false;
      const next = { ...prev };

      rows.forEach((row) => {
        const candidate = row.profile_name?.trim();
        if (!candidate || candidate.toLowerCase() === "not available") return;
        if (next[row.utilisateur] === candidate) return;

        next[row.utilisateur] = candidate;
        hasChange = true;
      });

      return hasChange ? next : prev;
    });
  }, [rows]);

  const rowsForThreads = useMemo(
    () =>
      searchTerm.trim() && serverSearchRows.length > 0
        ? mergeConversationRowsForRefresh(rows, serverSearchRows)
        : rows,
    [rows, searchTerm, serverSearchRows]
  );

  const threadsData = useMemo(() => {
    return rowsForThreads.map((r) => {
      // 1) Compteur renvoyé par la RPC (peut être 0 ou undefined)
      const rpcUnread = r.unread_count ?? 0;
  
      // 2) Interprétation "thread non lu" basée sur le compteur
      const fromCount = rpcUnread > 0;
  
      // 3) Interprétation "thread non lu" basée sur le flag lu du dernier message
      //    => si le dernier message de la conversation est lu = false,
      //       on considère le thread comme non lu, même si rpcUnread = 0.
      const fromLuFlag = (r.role === "user" || r.role === "assistant") && r.lu === false;
  
      const isUnread = fromCount || fromLuFlag;
  
      // 4) Compteur effectif pour l'affichage :
      //    - si la RPC en renvoie un, on le garde
      //    - sinon, si on sait que c'est non lu, on force à 1 (pastille verte)
      const effectiveUnreadCount = rpcUnread > 0 ? rpcUnread : isUnread ? 1 : 0;
  
      return {
        ...r,
        unreadCount: effectiveUnreadCount,
        isUnread,
      };
    });
  }, [rowsForThreads]);

  // Déchiffrement des previews (praticien)
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [botsOff, setBotsOff] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!practKey || user?.role === "admin") return;
    (async () => {
      const obj: Record<string, string> = {};
      for (const r of threadsData) {
        try {
          obj[r.utilisateur] = await decryptFernet(practKey, r.contenu_chiffre);
        } catch {
          obj[r.utilisateur] = "⛔️ contenu illisible";
        }
      }
      setPreviews(obj);
    })();
  }, [threadsData, practKey, user]);

  // Threads prêts pour l'affichage
  const threads: Thread[] = useMemo(() => {
    return threadsData.map((r) => ({
      utilisateur: r.utilisateur,
      displayPhone: r.utilisateur.replace(/^whatsapp:/, ""),
      displayName: (() => {
        const directoryName = getDisplayNameForPhone(
          r.utilisateur,
          r.praticien_id || currentPractitionerId
        );
        if (directoryName) return directoryName;
        
        const currentName = r.profile_name?.trim() || "";
        if (currentName && currentName.toLowerCase() !== "not available") return currentName;

        // 🔍 Fallback robuste: dernier nom mémorisé pour ce numéro
        const fallbackKnownName = knownNames[r.utilisateur];
        if (fallbackKnownName) return fallbackKnownName;
      
        // sinon on garde le numéro brut
        return r.utilisateur.replace(/^whatsapp:/, "");
      })(),
      lastText:
        user?.role === "admin"
          ? "★ ★ ★"
          : previews[r.utilisateur] ?? "⛔️ contenu illisible",
      lastAt: r.created_at,
      unreadCount: r.unreadCount,
      isUnread: r.isUnread,
      botOff: resolveConversationBotOff(r.utilisateur, r.botOff, botsOff),
      tag: r.tag,
      conversation_status: r.conversation_status ?? null,
      assigned_to: r.assigned_to,
      assignment_seen: r.assignment_seen,
      assignment_comment: r.assignment_comment,
    }));
  }, [
    botsOff,
    currentPractitionerId,
    getDisplayNameForPhone,
    knownNames,
    previews,
    threadsData,
    user,
  ]);

  const totalUnread = useMemo(
    () => threads.reduce((acc, t) => acc + (t.unreadCount || 0), 0),
    [threads]
  );

  useEffect(() => {
    if (!rows?.length || !assigneeId) {
      setAssignedCount(0);
      return;
    }

    const uniqueUsers = new Set<string>();
    rows.forEach((r) => {
      if (r.assigned_to === assigneeId && !r.assignment_seen) {
        uniqueUsers.add(r.utilisateur);
      }
    });

    setAssignedCount(uniqueUsers.size);
  }, [rows, assigneeId]);

  useEffect(() => {
    if (typeof totalUnread !== "number") return;

    if (totalUnread > 0) {
      if (!document.title.startsWith("(")) {
        document.title = `(${totalUnread}) ${document.title}`;
      } else {
        document.title = document.title.replace(/\(\d+\)\s+/, `(${totalUnread}) `);
      }
    } else {
      document.title = document.title.replace(/\(\d+\)\s+/, "");
    }
  }, [totalUnread]);

  // Recherche serveur sur 90 jours, sans charger tout l'historique dans la liste.
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term || !currentPractitionerId) {
      setSearchIndex({});
      setServerSearchRows([]);
      setServerSearchMatches({});
      setIsSearching(false);
      return;
    }
  
    let cancelled = false;
    const controller = new AbortController();
    setIsSearching(true);
  
    const timerId = window.setTimeout(() => {
      (async () => {
        try {
          const params = new URLSearchParams({
            praticien_id: currentPractitionerId,
            q: term,
            days: String(CONVERSATION_SEARCH_DAYS),
            limit: "100",
          });
          const token = await getAuthToken();
          const res = await fetch(
            `${import.meta.env.VITE_API_URL}/internal/conversations/search?${params.toString()}`,
            {
              headers: token ? { Authorization: token } : undefined,
              signal: controller.signal,
            }
          );
          if (!res.ok) throw new Error("Conversation search failed");

          const json = await res.json();
          const mapped = mapThreadResponse(json.threads || [], currentPractitionerId);
          const matches = Object.fromEntries(mapped.map((row) => [row.utilisateur, true]));

          if (!cancelled) {
            setServerSearchRows(mapped);
            setServerSearchMatches(matches);
            setRows((prev) => mergeConversationRowsForRefresh(prev, mapped));
            setSearchIndex({});
          }
        } catch (e: any) {
          if (e?.name !== "AbortError") {
            console.error("[search] global search error:", e);
          }
        } finally {
          if (!cancelled) setIsSearching(false);
        }
      })();
    }, CONVERSATION_SEARCH_DEBOUNCE_MS);
  
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
      controller.abort();
    };
  }, [currentPractitionerId, searchTerm]);

  const filteredThreads = useMemo(() => {
    // base: tous les threads
    let base = threads;

    // RBAC: si view_all_conversations absent, ne montrer que les conversations assignées au staff connecté
    if (activeStaff && !permissions.has("view_all_conversations")) {
      base = base.filter((t) => t.assigned_to === activeStaff.staff_id);
    }

    // filtres existants
    if (filter === "unread") base = base.filter((t) => t.isUnread);
    if (filter === "assigned") base = base.filter((t) => t.assigned_to === assigneeId);
    if (filter === "all_tasks") base = base.filter((t) => t.assigned_to !== null);

    // filtre par statut personnalisé (cumulatif avec les filtres ci-dessus)
    if (statusFilter) base = base.filter((t) => t.conversation_status === statusFilter);

    // 🔎 recherche: nom / numéro / contenu local ou correspondance serveur 90j.
    const term = searchTerm.trim().toLowerCase();
    if (!term) return base;

    return base.filter((t) => {
      const name = (t.displayName || "").toLowerCase();
      const phone = (t.displayPhone || t.utilisateur || "").toLowerCase();
      const content = (searchIndex[t.utilisateur] || t.lastText || "").toLowerCase();
      const serverMatched = Boolean(serverSearchMatches[t.utilisateur]);

      return serverMatched || name.includes(term) || phone.includes(term) || content.includes(term);
    });
  }, [threads, filter, statusFilter, activeStaff, permissions, searchTerm, searchIndex, serverSearchMatches]);

  async function markAssignmentSeen(utilisateur: string) {
    try {
      const targetId = assigneeId || user?.id;
  
      // ✅ Marquer comme vu ET libérer l'assignation
      const { error } = await supabase
        .from("conversations")
        .update({
          assignment_seen: true,
          assigned_to: null,
          assigned_at: null,
        })
        .eq("utilisateur", utilisateur)
        .eq("assigned_to", targetId);
  
      if (error) {
        console.error("[Assignment] Erreur Supabase :", error);
      } else {
        console.log(`[Assignment] assignment_seen & assigned_to=NULL pour ${utilisateur}`);
  
        // ✅ Mettre à jour localement (pour disparition immédiate du badge)
        setRows((prev) =>
          prev.map((r) =>
            r.utilisateur === utilisateur && r.assigned_to === targetId
              ? { ...r, assignment_seen: true, assigned_to: null, assigned_at: null }
              : r
          )
        );
      }
    } catch (error) {
      console.error("[Assignment] Exception assignment_seen :", error);
    }
  }

  async function clearTag(utilisateur: string, praticienId?: string) {
    try {
      const q = supabase.from("conversations").update({ tag: null }).eq("utilisateur", utilisateur);
      // on borne au praticien courant si non admin
      if (user?.role !== "admin" && praticienId) {
        q.eq("praticien_id", praticienId);
      }
      await q;
    } catch (error) {
      console.error("Erreur lors de la suppression du tag :", error);
    }
  }

  async function setConversationStatus(utilisateur: string, statusId: string | null) {
    // Update optimistically in local state
    setRows((prev) =>
      prev.map((r) =>
        r.utilisateur === utilisateur ? { ...r, conversation_status: statusId } : r
      )
    );
    try {
      const q = supabase
        .from("conversations")
        .update({ conversation_status: statusId })
        .eq("utilisateur", utilisateur);
      if (user?.role !== "admin" && user?.id) {
        q.eq("praticien_id", user.id);
      }
      await q;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut :", error);
    }
  }

  /* ────────────────────────────────
     Assignation (NOUVEAU)
  ──────────────────────────────── */
  async function assignToPractitioner(utilisateur: string, praticienId: string, comment?: string) {
    if (!praticienId) return;
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/push/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          utilisateur,
          praticien_id: praticienId,
          assigner_name: user?.email || "Staff",
          comment: comment || "",
          source_praticien_id: user?.id, // 🆕 important
        }),
      });
  
      if (!res.ok) throw new Error("Erreur lors de l'assignation");
      alert("Chat dispatched ✅");
    } catch (err) {
      console.error(err);
      alert("Error of dispatching ❌");
    }
  }

  async function loadMore() {
    // Si déjà en train de fetch → on stoppe
    if (isFetchingMore || daysWindow >= MAX_DAYS_WINDOW) return;
  
    setIsFetchingMore(true);
  
    const newWindow = Math.min(daysWindow + LOAD_MORE_DAYS_STEP, MAX_DAYS_WINDOW);
  
    const praticienId =
      user?.role === "admin" ? activeDoctor?.id : user?.id;
  
    if (!praticienId) {
      setIsFetchingMore(false);
      return;
    }
  
    try {
      const mapped = await fetchThreadsList(praticienId, newWindow);
      setRows((prev) => mergeConversationRowsForRefresh(prev, mapped));
      setDaysWindow(newWindow);
  
    } catch (error) {
      console.error("[INFINITE_SCROLL][ERROR]", error);
    } finally {
      // Delay prevents accidental double triggers.
      setTimeout(() => setIsFetchingMore(false), 600);
    }
  }

  const mobileLabelThread = mobileLabelSheetUser
    ? threads.find((thread) => thread.utilisateur === mobileLabelSheetUser) ?? null
    : null;
  const mobileActiveLabel = findConversationLabel(
    clinicStatuses,
    mobileLabelThread?.conversation_status ?? null
  );

  const openConversationThread = (thread: Thread) => {
    if (suppressNextConversationClickRef.current) {
      suppressNextConversationClickRef.current = false;
      return;
    }

    if (thread.isUnread && user) {
      supabase
        .from("conversations")
        .update({ lu: true })
        .eq("utilisateur", thread.utilisateur)
        .eq("praticien_id", user.id)
        .or("role.eq.assistant")
        .or("lu.is.null,lu.eq.false");
    }

    if (thread.tag) {
      setRows((prev) =>
        prev.map((r) =>
          r.utilisateur === thread.utilisateur &&
          (user?.role === "admin" || r.praticien_id === user?.id)
            ? { ...r, tag: null }
            : r
        )
      );
      clearTag(thread.utilisateur, user?.id);
    }

    if (onSelectItem) {
      onSelectItem(thread.utilisateur);
      return;
    }

    navigate(`/conversations/${encodeURIComponent(thread.utilisateur)}`);
  };

  return (
    <>
      <BotStatusListener practitionerId={currentPractitionerId} setBotsOff={setBotsOff} />
      <div className="flex flex-col h-full bg-white">
        {/* Header + Search — zone sticky unifiée */}
        <div className="sticky top-0 bg-white z-10 border-b border-slate-100">
          <div className="px-4 pt-4 pb-3 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Chats</h1>
            <button
              onClick={() => navigate("/new-message")}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
              title="New chat"
            >
              <MessageSquarePlus className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search name, phone or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60 transition-all"
              />
            </div>
            {isSearching && (
              <div className="text-xs text-slate-400 mt-1">Decrypting recent messages…</div>
            )}
          </div>
        </div>
  
        {/* Filter pills: All / Unread / My tasks / All tasks */}
        <div className="px-4 py-2 flex flex-wrap gap-2 text-sm border-b border-slate-100 items-center">
          <button
            type="button"
            onClick={() => setFilter("all")}
            aria-pressed={filter === "all"}
            className={`px-3 py-1 rounded-full border transition text-xs font-medium
              ${
                filter === "all"
                  ? "bg-primary text-white border-primary shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
              }`}
          >
            All
          </button>

          <button
            type="button"
            onClick={() => setFilter("unread")}
            aria-pressed={filter === "unread"}
            className={`px-3 py-1 rounded-full border transition flex items-center gap-1.5 text-xs font-medium
              ${
                filter === "unread"
                  ? "bg-primary text-white border-primary shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
              }`}
          >
            Unread
            {totalUnread > 0 && (
              <span className={`min-w-4 h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center
                ${filter === "unread" ? "bg-white/25 text-white" : "bg-primary text-white"}`}>
                {totalUnread > 9 ? "9+" : totalUnread}
              </span>
            )}
          </button>
          {hasMultiple && user?.role !== "admin" && (
            <button
              onClick={() => setFilter("assigned")}
              className={`px-3 py-1 rounded-full border transition flex items-center gap-1.5 text-xs font-medium ${
                filter === "assigned"
                  ? "bg-primary text-white border-primary shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
              }`}
            >
              My tasks
              {assignedCount > 0 && (
                <span className={`min-w-4 h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center
                  ${filter === "assigned" ? "bg-white/25 text-white" : "bg-primary text-white"}`}>
                  {assignedCount > 9 ? "9+" : assignedCount}
                </span>
              )}
            </button>
          )}
          {hasMultiple && (
            <button
              type="button"
              onClick={() => setFilter("all_tasks")}
              aria-pressed={filter === "all_tasks"}
              className={`px-3 py-1 rounded-full border transition text-xs font-medium
                ${
                  filter === "all_tasks"
                    ? "bg-primary text-white border-primary shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                }`}
            >
              All tasks
            </button>
          )}

          {/* Status filter dropdown */}
          {statusTagsEnabled && clinicStatuses.length > 0 && (() => {
            const activeStatus = findConversationLabel(clinicStatuses, statusFilter);
            return (
              <div ref={statusDropdownRef} className="relative flex-shrink-0">
                {/* Trigger button — taille fixe pour éviter le layout shift */}
                <button
                  type="button"
                  onClick={() => setStatusDropdownOpen((v) => !v)}
                  className={`h-[26px] px-3 rounded-full border transition text-xs font-medium flex items-center gap-1.5 whitespace-nowrap
                    ${statusFilter
                      ? "bg-primary text-white border-primary shadow-sm pr-1.5"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                    }`}
                >
                  {activeStatus ? (
                    <>
                      <span className="w-2 h-2 rounded-full flex-shrink-0 bg-white/70" />
                      <span className="max-w-[90px] truncate">{activeStatus.label}</span>
                      <span
                        role="button"
                        aria-label="Clear status filter"
                        onClick={(e) => { e.stopPropagation(); setStatusFilter(null); setStatusDropdownOpen(false); }}
                        className="flex items-center justify-center w-4 h-4 rounded-full bg-white/20 hover:bg-white/40 transition flex-shrink-0 ml-0.5"
                      >✕</span>
                    </>
                  ) : (
                    <>Label <span className="opacity-60">▾</span></>
                  )}
                </button>

                {statusDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 w-max min-w-[160px] max-w-[220px]">
                    {clinicStatuses.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => { setStatusFilter(statusFilter === s.id ? null : s.id); setStatusDropdownOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition hover:bg-slate-50
                          ${statusFilter === s.id ? "font-semibold text-slate-900" : "text-slate-700"}`}
                      >
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getConversationLabelDotClass(s.color)}`} />
                        <span className="truncate">{s.label}</span>
                        {statusFilter === s.id && <span className="ml-auto pl-2 text-primary flex-shrink-0">✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Reopening digest CTA — shown when feature enabled, clinic open, and no visible digest */}
        {digestFeatureEnabled && digestHasWindow && !digestVisible && (
          <ReopeningDigestCta
            isNew={!digestCurrent}
            loading={digestGenerating}
            onGenerate={handleGenerateDigest}
            onReopen={handleReopenDigest}
          />
        )}

        {/* Liste des threads (filtrée) */}
        <ul className="divide-y divide-slate-100">
          {/* Digest pseudo-thread — pinned at index 0, outside filters */}
          {digestFeatureEnabled && digestVisible && digestCurrent && (
            <li key="digest-row">
              <ReopeningDigestThreadRow
                digestId={digestCurrent.id}
                windowEnd={digestCurrent.window_end}
                metrics={digestCurrent.active_metrics ?? digestCurrent.metrics_json ?? {}}
                isSelected={!isMobile && selectedUser === `digest:${digestCurrent.id}`}
                onClick={() => {
                  const routeId = `digest:${digestCurrent.id}`;
                  if (onSelectItem) {
                    onSelectItem(routeId);
                  } else {
                    navigate(`/conversations/${encodeURIComponent(routeId)}`);
                  }
                }}
              />
            </li>
          )}
          <AnimatePresence mode="popLayout">
          {filteredThreads.map((t) => {
            const hasBookingTag = t.tag === "booking_detected";
            const isSelected =
              !isMobile && selectedUser && t.utilisateur === selectedUser;
            const hasStatusHighlight = Boolean(t.tag) || t.isUnread;

            return (
              <motion.li
                key={t.utilisateur}
                layout
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="relative group"
                onContextMenu={!isMobile && statusTagsEnabled && clinicStatuses.length > 0 ? (e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, utilisateur: t.utilisateur });
                } : undefined}
                onPointerDown={(event) => startMobileLabelLongPress(t, event)}
                onPointerMove={cancelMobileLabelLongPressOnMove}
                onPointerUp={cancelMobileLabelLongPress}
                onPointerCancel={cancelMobileLabelLongPress}
                onPointerLeave={cancelMobileLabelLongPress}
                onClickCapture={suppressConversationClickAfterLongPress}
              >
                {/* Ligne principale vers la conversation */}
                <button
                  type="button"
                  onClick={() => openConversationThread(t)}
                  data-route={`/conversations/${encodeURIComponent(t.utilisateur)}`}
                  aria-label={`Open conversation with ${t.displayName || t.displayPhone}`}
                  className={`flex w-full items-center gap-3 px-4 pr-14 py-3 text-left transition hover:bg-gray-50
                    ${isSelected ? "bg-gray-100/60 ring-1 ring-gray-300/60" : ""}
                    ${
                      isSelected && !hasStatusHighlight
                        ? "border-l-2 border-gray-200"
                        : ""
                    }
                    ${
                        t.tag === "booking_detected"
                          ? "bg-yellow-50 border-l-4 border-yellow-300"
                          : t.tag === "emergency"
                          ? "bg-red-50 border-l-4 border-red-400 animate-pulse"
                          : t.tag === "refill"
                          ? "bg-green-50 border-l-4 border-green-400"
                          : t.isUnread
                          ? getHighlightClasses(highlightUnread) || "border-l-4 border-blue-500 bg-blue-50"
                          : getHighlightClasses(highlightRead)
                    }
                  `}
                >
                  {/* Avatar */}
                  {(() => {
                    const palette = getAvatarPalette(t.utilisateur);
                    const initial = t.displayName !== "—" ? t.displayName[0] : "?";
                    return (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ring-1 flex-shrink-0 ${palette.bg} ${palette.text} ${palette.ring}`}>
                        {initial}
                      </div>
                    );
                  })()}

                  {/* Nom + preview */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`truncate text-sm ${t.isUnread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>
                        {t.displayName !== "—" ? t.displayName : t.displayPhone}
                      </div>

                      <div className="ml-auto flex items-center gap-2 relative flex-shrink-0">
                        {/* Indicateur Bot OFF */}
                        {t.botOff && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-600 border border-red-200"
                            title="Bot désactivé"
                          >
                            Bot off
                          </span>
                        )}

                        {/* Heure */}
                        <div className={`text-[11px] tabular-nums ${t.isUnread ? "text-primary font-medium" : "text-slate-400"}`}>
                          {formatLastAt(t.lastAt)}
                        </div>

                        {/* Pastille non-lu */}
                        {t.isUnread && (
                          <span className="min-w-4 h-4 rounded-full bg-primary text-white text-[10px] font-semibold flex items-center justify-center px-1">
                            {t.unreadCount > 9 ? "9+" : t.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={`text-xs truncate ${t.isUnread ? "text-slate-700" : "text-slate-400"}`}>
                      {t.lastText}
                    </div>

                    {t.tag === "booking_detected" && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200/80">
                        <CalendarDays className="w-2.5 h-2.5" />
                        Appointment request
                      </span>
                    )}

                    {t.tag === "emergency" && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200/80">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Emergency
                      </span>
                    )}

                    {t.tag === "refill" && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 border border-green-200/80">
                        <Pill className="w-2.5 h-2.5" />
                        Refill request
                      </span>
                    )}

                    {statusTagsEnabled && t.conversation_status && (() => {
                      const s = findConversationLabel(clinicStatuses, t.conversation_status);
                      if (!s) return null;
                      return (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getConversationLabelDotClass(s.color)}`} />
                          {s.label}
                        </span>
                      );
                    })()}
              
                    {hasMultiple && t.assigned_to && (
                      <div className="mt-1 text-xs space-y-1">
                        {t.assigned_to === assigneeId && !t.assignment_seen ? (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                            Assigned to you
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                            Assigned to {practitioners.find((p) => p.id === t.assigned_to)?.full_name || "someone"}
                          </span>
                        )}
                    
                        {/* 👇 Affichage du commentaire si présent */}
                        {t.assignment_comment && (
                          <div className="text-gray-500 italic text-[11px] bg-gray-50 rounded-md p-1 border border-gray-100">
                            “{t.assignment_comment}”
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </button>

                {/* 🔹 Popover déplacé EN DEHORS de la ligne principale */}
                {hasMultiple && user?.role !== "admin" && (
                  <div className="absolute inset-y-0 right-3 flex items-center">
                    <Popover.Root>
                      <Popover.Trigger asChild>
                        <button
                          className="p-1 rounded-full text-gray-400 hover:text-slate-700 hover:bg-gray-100 transition opacity-0 group-hover:opacity-100"
                          title="Assign conversation"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                        </button>
                      </Popover.Trigger>
                      <Popover.Portal>
                        <Popover.Content
                          side="top"
                          align="end"
                          className="z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 p-2 w-48 animate-in fade-in slide-in-from-bottom-2"
                        >
                          <p className="text-xs font-semibold text-gray-500 px-2 mb-1">Assign to:</p>
                          <ul className="space-y-1">
                            {practitioners.map((p) => (
                              <li key={p.id}>
                                <button
                                  onClick={() => {
                                    setSelectedPractitioner(p);
                                    setSelectedAssignUser(t.utilisateur);
                                    setShowAssignDialog(true); // 👈 ouvre la modal
                                  }}
                                  className="w-full text-left px-2 py-1.5 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition"
                                >
                                  {p.full_name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </Popover.Content>
                      </Popover.Portal>
                    </Popover.Root>
                  </div>
                )}
              </motion.li>
            );
          })}
          </AnimatePresence>
          {filteredThreads.length === 0 && (
            <li className="px-4 py-10 text-center text-slate-400 text-sm">No conversation.</li>
          )}
          <li className="py-4 text-center">
            <button
              onClick={loadMore}
              disabled={isFetchingMore || daysWindow >= MAX_DAYS_WINDOW}
              className="px-4 py-2 text-sm rounded-full border bg-gray-50 hover:bg-gray-100 disabled:opacity-50"
            >
              {isFetchingMore
                ? "Loading…"
                : daysWindow >= MAX_DAYS_WINDOW
                ? "90-day history loaded"
                : "Load previous 48h"}
            </button>
          </li>
        </ul>
        {/* 🔹 Right-click status context menu */}
        {contextMenu && (
          <StatusContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            statuses={clinicStatuses}
            currentStatus={threads.find((t) => t.utilisateur === contextMenu.utilisateur)?.conversation_status ?? null}
            onSelect={async (statusId) => {
              await setConversationStatus(contextMenu.utilisateur, statusId);
              setContextMenu(null);
            }}
            onClose={() => setContextMenu(null)}
          />
        )}

        {/* Mobile label picker */}
        <Dialog.Root
          open={Boolean(mobileLabelThread)}
          onOpenChange={(open) => {
            if (!open) setMobileLabelSheetUser(null);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[9998] bg-black/35" />
            {mobileLabelThread && (
              <Dialog.Content className="fixed inset-x-0 bottom-0 z-[9999] max-h-[80dvh] overflow-y-auto rounded-t-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-200" />
                <Dialog.Title className="text-base font-semibold text-slate-900">
                  Set conversation label
                </Dialog.Title>
                <p className="mt-1 text-sm text-slate-500">
                  {mobileLabelThread.displayName || mobileLabelThread.displayPhone}
                </p>

                <div className="mt-4 space-y-1">
                  {clinicStatuses.map((status) => {
                    const isActive = mobileLabelThread.conversation_status === status.id;
                    return (
                      <button
                        key={status.id}
                        type="button"
                        onClick={async () => {
                          await setConversationStatus(
                            mobileLabelThread.utilisateur,
                            isActive ? null : status.id
                          );
                          setMobileLabelSheetUser(null);
                        }}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition
                          ${
                            isActive
                              ? "bg-primary/5 font-semibold text-slate-900"
                              : "text-slate-700 hover:bg-slate-50"
                          }`}
                      >
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${getConversationLabelDotClass(status.color)}`} />
                        <span className="min-w-0 flex-1 truncate">{status.label}</span>
                        {isActive && <span className="text-xs font-medium text-primary">Active</span>}
                      </button>
                    );
                  })}
                </div>

                {mobileActiveLabel && (
                  <button
                    type="button"
                    onClick={async () => {
                      await setConversationStatus(mobileLabelThread.utilisateur, null);
                      setMobileLabelSheetUser(null);
                    }}
                    className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                  >
                    Clear label
                  </button>
                )}

                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="mt-2 w-full rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 transition hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
              </Dialog.Content>
            )}
          </Dialog.Portal>
        </Dialog.Root>

        {/* Assignment comment dialog */}
        <Dialog.Root open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998]" />
            <Dialog.Content
              className="fixed top-[50%] left-[50%] w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl border p-6 space-y-4 z-[9999]"
            >
              <Dialog.Title className="text-lg font-semibold text-slate-800">
                Assign conversation
              </Dialog.Title>
        
              {selectedPractitioner && (
                <p className="text-sm text-gray-600">
                  To: <span className="font-medium text-slate-800">{selectedPractitioner.full_name}</span>
                </p>
              )}
        
              <textarea
                placeholder="Add a short comment or instruction..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full h-24 p-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-400 text-sm"
              />
        
              <div className="flex justify-end gap-2 pt-2">
                <Dialog.Close asChild>
                  <button
                    className="px-4 py-1.5 rounded-md border text-gray-600 hover:bg-gray-100 transition"
                    onClick={() => {
                      setComment("");
                      setSelectedPractitioner(null);
                      setSelectedAssignUser(null);
                    }}
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  onClick={async () => {
                    if (selectedAssignUser && selectedPractitioner) {
                      await assignToPractitioner(selectedAssignUser, selectedPractitioner.id, comment);
                    }
                    setComment("");
                    setSelectedPractitioner(null);
                    setSelectedAssignUser(null);
                    setShowAssignDialog(false);
                  }}
                  className="px-4 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-700 transition"
                >
                  Assign
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </>
  );
}
